import {
	RewardEngineOption,
	TreatmentSegment,
} from "../../domain/treatmentPlan.interface";

/**
 * SIMS-7151 (FR-3) — pure derivation logic for the plan-level segment accordion editor.
 *
 * Kept DOM-free so it is unit-tested directly (jest, no RTL): the accordion rendering itself lives
 * in ./index.tsx (a component index, excluded from coverage), and calls into these helpers. All
 * functions are immutable — they never mutate the passed segments array. Synthetic data only.
 */

/** The empty display value for a reward engine that is unset / not found. */
export const NO_REWARD_ENGINE = "None";

/** True when the plan actually has one or more segments (absent and [] are treated identically). */
export function hasSegments(segments?: TreatmentSegment[]): boolean {
	return Array.isArray(segments) && segments.length > 0;
}

/**
 * The starting week for the NEXT segment: one past the last segment's end, or week 1 when there
 * are none. Keeps newly-added segments contiguous by default (full contiguity validation is 7167).
 */
export function nextSegmentStartWeek(segments: TreatmentSegment[]): number {
	if (!segments.length) {
		return 1;
	}
	const last = segments[segments.length - 1];
	return (last.weekRange?.end ?? 0) + 1;
}

/** A new structural-only segment spanning a single week starting at `startWeek`. */
export function makeSegment(startWeek: number): TreatmentSegment {
	return {
		weekRange: { start: startWeek, end: startWeek },
		visitsPerWeek: 1,
		minHoursBetweenVisits: 0,
	};
}

/** Append a new contiguous segment (AC1). */
export function appendSegment(
	segments: TreatmentSegment[],
): TreatmentSegment[] {
	return [...segments, makeSegment(nextSegmentStartWeek(segments))];
}

/** Remove the segment at `index` (immutably). */
export function removeSegment(
	segments: TreatmentSegment[],
	index: number,
): TreatmentSegment[] {
	return segments.filter((_, i) => i !== index);
}

/** Immutably patch the segment at `index`. */
export function updateSegment(
	segments: TreatmentSegment[],
	index: number,
	patch: Partial<TreatmentSegment>,
): TreatmentSegment[] {
	return segments.map((seg, i) => (i === index ? { ...seg, ...patch } : seg));
}

/**
 * The effective reward engine for a segment: its override if set, else the template default.
 * Forward-compatible with the 7159 override toggle (segments carry no override yet in 7151).
 */
export function effectiveRewardEngineId(
	segment: TreatmentSegment,
	templateDefaultRewardEngineId?: string,
): string | undefined {
	return segment.rewardEngineId || templateDefaultRewardEngineId || undefined;
}

/** Human-readable reward engine name for an id, or "None" when unset / not found. */
export function rewardEngineName(
	id: string | undefined,
	options: RewardEngineOption[],
): string {
	if (!id) {
		return NO_REWARD_ENGINE;
	}
	return options.find((opt) => opt.id === id)?.name ?? NO_REWARD_ENGINE;
}

/** "Weeks {start}–{end}" (en dash), or "Weeks —" when the range is missing. */
export function formatWeekRange(segment: TreatmentSegment): string {
	const wr = segment.weekRange;
	if (!wr || wr.start == null || wr.end == null) {
		return "Weeks —";
	}
	return `Weeks ${wr.start}–${wr.end}`;
}

/**
 * The collapsed accordion header (AC2): segment number, week range, visits/week, and the effective
 * reward engine name — separated by middots.
 */
export function segmentHeaderSummary(
	segment: TreatmentSegment,
	index: number,
	options: RewardEngineOption[],
	templateDefaultRewardEngineId?: string,
): string {
	const engine = rewardEngineName(
		effectiveRewardEngineId(segment, templateDefaultRewardEngineId),
		options,
	);
	return `Segment ${index + 1} · ${formatWeekRange(segment)} · ${segment.visitsPerWeek}/wk · ${engine}`;
}

/**
 * Extract the persisted segments for pre-population (AC3). The backend folds the plan's segments
 * into treatmentConfig.segments (read shape: response.data.treatmentConfig.segments); a
 * non-segmented plan has an absent / non-array value, which yields [] (same as empty — AC4).
 */
export function readPlanSegments(
	treatmentConfig?: Record<string, unknown>,
): TreatmentSegment[] {
	const raw = treatmentConfig?.segments;
	return Array.isArray(raw) ? (raw as TreatmentSegment[]) : [];
}

// ---------------------------------------------------------------------------
// SIMS-7159 (FR-4, FR-6) — per-segment override toggles + reset-ladder lock.
// ---------------------------------------------------------------------------

/** The optional per-segment override fields exposed as toggle-driven inputs (FR-4). */
export type SegmentOverrideField =
	| "rewardEngineId"
	| "behavior"
	| "cmVisitScheduleOptionIds"
	| "disenrollmentDaysThreshold";

/** The string-valued override fields (their empty value is ""); disenrollment is numeric. */
const STRING_OVERRIDE_FIELDS: SegmentOverrideField[] = [
	"rewardEngineId",
	"behavior",
	"cmVisitScheduleOptionIds",
];

const OVERRIDE_LABELS: Record<SegmentOverrideField, string> = {
	rewardEngineId: "reward engine",
	behavior: "behavior",
	cmVisitScheduleOptionIds: "visit schedule",
	disenrollmentDaysThreshold: "disenrollment threshold",
};

/**
 * An override is ACTIVE (toggle ON) when its key is present on the segment. Toggle OFF removes the
 * key so the field inherits the template default (AC1); an existing persisted override reads back
 * as ON (pre-population).
 */
export function isOverrideActive(
	segment: TreatmentSegment,
	field: SegmentOverrideField,
): boolean {
	return segment[field] !== undefined;
}

/** Set an override value on a segment (immutably) — toggle ON + edit persists to segments[n] (AC2). */
export function setOverride(
	segments: TreatmentSegment[],
	index: number,
	field: SegmentOverrideField,
	value: string | number,
): TreatmentSegment[] {
	return updateSegment(segments, index, {
		[field]: value,
	} as Partial<TreatmentSegment>);
}

/** Clear an override (immutably) — toggle OFF removes the key so nothing is persisted (AC1). */
export function clearOverride(
	segments: TreatmentSegment[],
	index: number,
	field: SegmentOverrideField,
): TreatmentSegment[] {
	return updateSegment(segments, index, {
		[field]: undefined,
	} as Partial<TreatmentSegment>);
}

/**
 * SIMS-7151 (AC1) — the STRUCTURAL fields must be usable before a plan can be saved.
 *
 * The `required` prop on those TextFields is presentational only: the form submits through
 * handleSubmit, not native validation, so an emptied field silently reached the server. And
 * because `patchNumber` coerces "" to 0, an emptied Visits Per Week persisted as 0 — which makes
 * `segmentVisitCount` zero, so `resolveSegmentConfig`'s cumulative pointer never advances past
 * that segment. The segment an admin just configured then NEVER APPLIES to any visit, with no
 * error anywhere. Found by E2E C15606.
 *
 * A reversed range (end < start) is rejected for the same reason: it yields a NEGATIVE visit
 * count, which is strictly worse than the zero case.
 *
 * Note `minHoursBetweenVisits` legitimately allows 0 (its input sets min={0}), so an emptied
 * value is indistinguishable from a deliberate 0 and cannot be treated as missing here.
 */
export function validateSegmentStructure(
	segments: TreatmentSegment[],
): string | null {
	for (let i = 0; i < segments.length; i++) {
		const seg = segments[i];
		// Typed `number | undefined` on purpose. The interface declares weekRange (and its
		// start/end) as required, but this validator exists precisely because the form can hand
		// us a half-filled segment — an emptied TextField yields undefined, and a plan loaded from
		// the server may predate the field. Declaring the widened type lets the `typeof` guards
		// below narrow to `number`, instead of asserting a type we do not actually have
		// (Number.isFinite takes `unknown` and narrows nothing).
		const start: number | undefined = seg.weekRange?.start;
		const end: number | undefined = seg.weekRange?.end;

		if (typeof start !== "number" || !Number.isFinite(start) || start < 1) {
			return `Segment ${i + 1}: start week is required and must be at least 1.`;
		}
		if (typeof end !== "number" || !Number.isFinite(end) || end < start) {
			return `Segment ${i + 1}: end week is required and cannot be before the start week.`;
		}
		// The two checks below deliberately have NO `typeof` guard, unlike start/end above. The
		// asymmetry is a TypeScript one, not a runtime one: start/end are read through optional
		// chaining into `number | undefined` locals, so TS needs the `typeof` to narrow them before
		// `<` is allowed, whereas visitsPerWeek/minHoursBetweenVisits are declared plain `number` on
		// TreatmentSegment and read directly, so nothing needs narrowing.
		//
		// DO NOT drop the Number.isFinite call as redundant — the two checks catch DIFFERENT inputs.
		// An emptied TextField never arrives here as "": patchNumber (SegmentEditor/index.tsx) maps
		// raw === "" to 0, so the emptied-in-the-form case is a 0 and it is the `< 1` comparison that
		// rejects it. What Number.isFinite covers is every shape that is not a usable number at all —
		// NaN (patchNumber's `Number(raw)` on a non-numeric string) and undefined/null (a plan loaded
		// from the server that predates the field, or a segment built outside this form). Those must
		// not fall through to the comparison alone: any comparison against NaN is false, and
		// `undefined < 1` is false too, so `< 1` / `< 0` on its own would accept all of them.
		// Number.isFinite is the right predicate because it does NOT coerce, unlike the global
		// isFinite — global isFinite(null) and isFinite("") are both true.
		if (!Number.isFinite(seg.visitsPerWeek) || seg.visitsPerWeek < 1) {
			return `Segment ${i + 1}: visits per week is required and must be at least 1.`;
		}
		if (
			!Number.isFinite(seg.minHoursBetweenVisits) ||
			seg.minHoursBetweenVisits < 0
		) {
			return `Segment ${i + 1}: min hours between visits is required and cannot be negative.`;
		}
	}
	return null;
}

/**
 * AC2 (C15598): an override that is toggled ON but left empty/invalid blocks save. Returns a
 * descriptive error for the first offending segment, or null when all active overrides have a
 * value. A string override is empty when it trims to ""; the disenrollment override must be > 0.
 */
export function validateSegmentOverrides(
	segments: TreatmentSegment[],
): string | null {
	for (let i = 0; i < segments.length; i++) {
		const seg = segments[i];
		for (const field of STRING_OVERRIDE_FIELDS) {
			const value = seg[field];
			if (value !== undefined && String(value).trim() === "") {
				return `Segment ${i + 1}: the ${OVERRIDE_LABELS[field]} override is enabled but has no value.`;
			}
		}
		if (
			seg.disenrollmentDaysThreshold !== undefined &&
			!(seg.disenrollmentDaysThreshold > 0)
		) {
			return `Segment ${i + 1}: the ${OVERRIDE_LABELS.disenrollmentDaysThreshold} override must be a positive number.`;
		}
	}
	return null;
}

/**
 * The previous segment's effective reward engine (its override, else the template default), or
 * undefined for the first segment (no previous to compare against).
 */
export function previousEffectiveRewardEngineId(
	segments: TreatmentSegment[],
	index: number,
	templateDefaultRewardEngineId?: string,
): string | undefined {
	if (index <= 0) {
		return undefined;
	}
	return effectiveRewardEngineId(
		segments[index - 1],
		templateDefaultRewardEngineId,
	);
}

/**
 * AC3/AC4 (FR-6): resetLadderOnEntry is LOCKED (forced true) when this segment's effective reward
 * engine differs from the previous segment's effective reward engine — a reward-engine change on
 * entry always resets the ladder. The first segment (no previous) is never locked. When the engines
 * match, the toggle is freely configurable.
 */
export function isResetLadderLocked(
	segments: TreatmentSegment[],
	index: number,
	templateDefaultRewardEngineId?: string,
): boolean {
	const previous = previousEffectiveRewardEngineId(
		segments,
		index,
		templateDefaultRewardEngineId,
	);
	if (previous === undefined) {
		return false;
	}
	const current = effectiveRewardEngineId(
		segments[index],
		templateDefaultRewardEngineId,
	);
	return current !== previous;
}

/**
 * The effective resetLadderOnEntry value: forced true when locked (AC3), otherwise the segment's
 * own toggle value (default false) (AC4).
 */
export function resolveResetLadderValue(
	segments: TreatmentSegment[],
	index: number,
	templateDefaultRewardEngineId?: string,
): boolean {
	if (isResetLadderLocked(segments, index, templateDefaultRewardEngineId)) {
		return true;
	}
	return segments[index].resetLadderOnEntry ?? false;
}

// ---------------------------------------------------------------------------
// SIMS-7167 (FR-3, FR-5) — client-side week-range contiguity validation.
// ---------------------------------------------------------------------------

/** A contiguity violation, carrying the 0-based index of the offending segment for inline display. */
export interface SegmentContiguityError {
	index: number;
	message: string;
}

/**
 * Validate that segment week ranges are internally consistent: the first segment starts at week 1
 * (AC5), and each subsequent segment is contiguous with the previous one — no gap (AC2), no overlap
 * (AC3). Returns the FIRST violation (with the offending segment index) or null when valid (AC1).
 * Mirrors the backend validateSegmentWeekRanges (SIMS-7143) so the UI blocks before the server does.
 * Total-duration coverage is NOT checked here (derived from segments — SIMS-7487).
 */
export function validateSegmentContiguity(
	segments: TreatmentSegment[],
): SegmentContiguityError | null {
	if (segments.length === 0) {
		return null;
	}

	// AC5 — the first segment must start at week 1.
	if (segments[0].weekRange?.start !== 1) {
		return {
			index: 0,
			message: `Segment 1 must start at week 1, but starts at week ${segments[0].weekRange?.start ?? "?"}.`,
		};
	}

	for (let i = 1; i < segments.length; i++) {
		const previousEnd = segments[i - 1].weekRange?.end ?? 0;
		const currentStart = segments[i].weekRange?.start ?? 0;
		const expectedStart = previousEnd + 1;

		// AC2 — a gap: the current segment starts after the previous one ended + 1.
		if (currentStart > expectedStart) {
			return {
				index: i,
				message: `Gap before segment ${i + 1}: it must start at week ${expectedStart} (segment ${i} ends at week ${previousEnd}).`,
			};
		}

		// AC3 — an overlap: the current segment starts before the previous one ended + 1.
		if (currentStart < expectedStart) {
			return {
				index: i,
				message: `Segment ${i + 1} overlaps segment ${i}: it must start at week ${expectedStart} (segment ${i} ends at week ${previousEnd}).`,
			};
		}
	}

	return null;
}

/** The contiguity error message for the save-block, or null when the ranges are valid. */
export function contiguityErrorMessage(
	segments: TreatmentSegment[],
): string | null {
	return validateSegmentContiguity(segments)?.message ?? null;
}
